import MenuIcon from '@mui/icons-material/Menu'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink, NavLink, Outlet } from 'react-router-dom'

import { useMe } from '@/api/session'
import { Footer } from '@/components/Footer'
import { Mark } from '@/components/Mark'
import { NewCampaignButton } from '@/components/NewCampaignButton'
import { tokens } from '@/theme'

const NAV = [
  { to: '/campaigns', label: 'Campaigns', end: true },
  { to: '/archive', label: 'Archive', end: false },
  { to: '/people', label: 'People', end: false },
]

/**
 * First and last initial, which is what a person recognises themselves by.
 *
 * Last word rather than second: "Maria del Carmen Santos" should read MS, not
 * MD. A single name gives one letter rather than a doubled one.
 */
function initials(name: string | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean)
  const first = words.at(0)?.charAt(0) ?? ''
  const last = words.length > 1 ? (words.at(-1)?.charAt(0) ?? '') : ''
  return (first + last).toUpperCase()
}

/**
 * The frame every signed-in screen sits in.
 *
 * One width for every screen — a single max-width container rather than
 * per-page guesses, which is what made v1 read like three different apps
 * wearing the same colours.
 */
export function Shell() {
  const me = useMe()
  // Anchor for the small-screen nav. Null is closed; MUI wants the element
  // itself rather than a boolean so the menu can position against it.
  const [navAnchor, setNavAnchor] = useState<HTMLElement | null>(null)

  const nav = me.data?.is_platform_admin
    ? [...NAV, { to: '/admin', label: 'Platform', end: false }]
    : NAV

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        color="transparent"
        sx={{
          bgcolor: tokens.bg,
          borderBottom: `1px solid ${tokens.line}`,
          backdropFilter: 'saturate(180%) blur(8px)',
        }}
      >
        <Container maxWidth="lg" disableGutters>
          {/* Gaps and padding shrink before anything is hidden: on a 393px
            * iPhone the bar was 516px wide, which does not clip neatly — it
            * makes the WHOLE PAGE that wide, so every screen scrolled
            * sideways and the New button sat off the edge. */}
          <Toolbar
            variant="dense"
            sx={{ gap: { xs: 1, md: 3 }, minHeight: 48, px: { xs: 1.5, md: 2 } }}
          >
            <Stack
              component={RouterLink}
              to="/campaigns"
              direction="row"
              alignItems="center"
              spacing={0.875}
              sx={{ textDecoration: 'none' }}
            >
              <Mark size={18} />
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.9375rem',
                  letterSpacing: '-0.02em',
                  color: 'text.primary',
                }}
              >
                Fronted
              </Typography>
            </Stack>

            {/* Below md the three destinations collapse into this rather than
              * wrapping onto a second row: the bar is sticky, and a two-row
              * sticky header eats a fifth of an iPhone screen on every
              * scroll. */}
            <IconButton
              aria-label="Menu"
              onClick={(e) => setNavAnchor(e.currentTarget)}
              sx={{ display: { xs: 'inline-flex', md: 'none' }, ml: -0.5 }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>
            <Menu
              anchorEl={navAnchor}
              open={Boolean(navAnchor)}
              onClose={() => setNavAnchor(null)}
            >
              {nav.map((item) => (
                <MenuItem
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.end}
                  onClick={() => setNavAnchor(null)}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
            {/* Takes the space the hidden nav row would have taken, so New
              * and the avatar stay right-aligned. */}
            <Box sx={{ flex: 1, display: { xs: 'block', md: 'none' } }} />

            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }}
            >
              {NAV.map((item) => (
                <Button
                  key={item.to}
                  component={NavLink}
                  to={item.to}
                  end={item.end}
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 520,
                    '&.active': {
                      color: 'text.primary',
                      bgcolor: tokens.surface,
                    },
                  }}
                >
                  {item.label}
                </Button>
              ))}
              {me.data?.is_platform_admin && (
                <Button
                  component={NavLink}
                  to="/admin"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 520,
                    '&.active': { color: 'text.primary', bgcolor: tokens.surface },
                  }}
                >
                  Platform
                </Button>
              )}
            </Stack>

            {/* Two doors, one button. The bar has room for exactly one
                contained control on a phone, so the second shape lives in a
                menu rather than beside it — and a manager with no campaigns
                at all gets both as real buttons on the empty state, which is
                the moment of highest intent. */}
            <NewCampaignButton />

            {me.data?.signed_in && (
              /* Initials, not the full name — the bar is chrome and a name of
               * unpredictable length pushed the layout around. But an unnamed
               * account keeps the words: a blank avatar gives somebody who has
               * never set a name no reason to click, and that name is printed
               * on every pay page as who nine people owe money to. */
              <Stack
                component={RouterLink}
                to="/profile"
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ textDecoration: 'none', ml: 0.5 }}
              >
                {!me.data.display_name && (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}
                  >
                    Set your name
                  </Typography>
                )}
                <Tooltip title={me.data.display_name || 'Set your name'}>
                  <Avatar
                    aria-label={
                      me.data.display_name
                        ? `Your profile — ${me.data.display_name}`
                        : 'Your profile — no name set'
                    }
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      // Quiet on purpose. "New campaign" beside it is the one
                      // saturated thing on the bar, and two accents next to
                      // each other means neither is the primary action.
                      bgcolor: tokens.surface,
                      color: 'text.primary',
                      border: `1px solid ${tokens.line}`,
                      '&:hover': { borderColor: 'text.secondary' },
                    }}
                  >
                    {initials(me.data.display_name) || (
                      <PersonOutlineIcon sx={{ fontSize: 16 }} />
                    )}
                  </Avatar>
                </Tooltip>
              </Stack>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 3, px: 2 }}>
        <Outlet />
        <Footer />
      </Container>
    </Box>
  )
}
